import { useState } from "react";
import { ApiClientError } from "../../lib/api/client";
import {
  useCreateSettingsCategoryMutation,
  useSettingsCategoriesQuery,
} from "../../lib/api/settings";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { Modal } from "../../components/ui/Modal";

export function CategoriesPanel() {
  const categories = useSettingsCategoriesQuery(true);
  const createCategory = useCreateSettingsCategoryMutation();
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<"income" | "expense">("expense");
  const [error, setError] = useState<string | null>(null);

  async function saveCategory() {
    setError(null);
    try {
      await createCategory.mutateAsync({ name, type });
      setName("");
      setAddOpen(false);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not add category.");
    }
  }

  return (
    <>
      <Card padding="md">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-edlab-ink">Income & expense categories</h2>
          <Button
            size="small"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            Add category
          </Button>
        </div>

        {categories.isLoading ? (
          <LoadingSpinner label="Loading categories…" className="mt-6" />
        ) : (
          <div className="category-checks mt-5">
            {categories.data?.categories.map((category) => (
              <div key={category.id} className="category-check">
                <span aria-hidden className="text-edlab-muted">
                  📁
                </span>
                <span>{category.name}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        title="Add category"
        description="Categories appear in income and expense allocation lines."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setAddOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={createCategory.isPending || !name.trim()}
              onClick={() => void saveCategory()}
            >
              Add category
            </Button>
          </>
        }
      >
        {error ? <p className="edlab-error mb-4">{error}</p> : null}
        <div className="grid gap-3">
          <label className="form-field">
            Name
            <input
              className="edlab-input"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </label>
          <label className="form-field">
            Type
            <select
              className="edlab-select"
              value={type}
              onChange={(event) => {
                setType(event.target.value as "income" | "expense");
              }}
            >
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
          </label>
        </div>
      </Modal>
    </>
  );
}
